import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, OnInit, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, firstValueFrom, fromEvent } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { buildPaginationItems, buildPaginationLabel, buildQueryString, buildTimestampFileName, canShowPagination, downloadBlob, extractPagination, getApiErrorMessage } from '../../../helper/table-utils';
import { Commonservice } from '../../../services/commonservice';
import { Httpservice } from '../../../services/httpservice';
import { AppPreferencesService } from '../../../services/app-preferences';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { ManageGeneratorAutocompleteComponent, ManageGeneratorAutocompleteCompleteEvent } from '../manage-generator/manage-generator-autocomplete';

type ModalMode = 'create' | 'edit' | 'view';
type SortField = 'entity_name' | 'short_code' | 'valid_upto' | 'is_active';
type SortOrder = 'asc' | 'desc';

interface EntityOption { id: number; entity_name: string; short_code: string | null; }
interface ApiAccessToken { id: number; serial_no: number; entity_id: number; entity_name: string; short_code: string; token: string; valid_upto: string | null; is_active: boolean | string | number | null; }
interface Pagination { page_no: number; page_size: number; total_records: number; total_pages: number; }
interface BsCalendarCell { bs_date: string; bs_date_label: string; ad_date: string; in_current_month: boolean; is_today: boolean; is_selected: boolean; }
interface BsCalendarMonth { bs_year: number; bs_month: number; bs_month_name: string; cells: BsCalendarCell[]; }

function localDateTimeValue(date = new Date()): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function toDateTimeInputValue(value: string | null): string {
  if (!value) return localDateTimeValue();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? localDateTimeValue() : localDateTimeValue(date);
}

@Component({
  selector: 'app-api-access-token-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent, ManageGeneratorAutocompleteComponent],
  templateUrl: './api-access-token.html',
  styleUrl: './api-access-token.scss',
})
export class ApiAccessTokenPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly filterForm: UntypedFormGroup;
  protected readonly tokenForm: UntypedFormGroup;
  protected readonly pageSizeControl: FormControl;
  protected readonly minValidUpto = localDateTimeValue();
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected isGenerating = false;
  protected modalOpen = false;
  protected modalMode: ModalMode = 'create';
  protected formSubmitted = false;
  protected submitErrorMessage = '';
  protected tokenGenerated = false;
  protected selectedToken: ApiAccessToken | null = null;
  protected entities: EntityOption[] = [];
  protected filteredEntities: EntityOption[] = [];
  protected organisationSearchTerm = '';
  protected bsCalendarOpen = false;
  protected bsCalendarLoading = false;
  protected bsCalendarMonth: BsCalendarMonth | null = null;
  protected bsValidUptoDate = '';
  protected bsValidUptoTime = localDateTimeValue().slice(11, 16);
  protected adValidUptoDate = localDateTimeValue().slice(0, 10);
  protected adValidUptoTime = localDateTimeValue().slice(11, 16);
  protected tokens: ApiAccessToken[] = [];
  protected sortBy: SortField = 'entity_name';
  protected sortOrder: SortOrder = 'asc';
  protected pagination: Pagination = { page_no: 1, page_size: 15, total_records: 0, total_pages: 0 };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject(ElementRef<HTMLElement>);

  constructor(private readonly fb: UntypedFormBuilder, private readonly httpSvc: Httpservice, private readonly commonSvc: Commonservice, private readonly preferencesSvc: AppPreferencesService) {
    this.filterForm = this.fb.group({ search: [''], page_size: [15] });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;
    this.tokenForm = this.fb.group({
      entity_id: [null, Validators.required],
      short_code: [{ value: '', disabled: true }],
      token: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(255)]],
      valid_upto: [localDateTimeValue(), Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadEntities();
    this.loadTokens();
    this.listenToDatePreferenceChanges();
    this.filterForm.valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadTokens(1));
    this.tokenForm.get('entity_id')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((entityId) => this.updateShortCode(entityId));
  }

  protected handleMenuToggle(): void { if (typeof window !== 'undefined' && window.innerWidth >= 1024) this.sidebarCollapsed = !this.sidebarCollapsed; else this.mobileSidebarOpen = !this.mobileSidebarOpen; }
  protected closeMobileSidebar(): void { this.mobileSidebarOpen = false; }
  protected get isBsDateMode(): boolean { return this.preferencesSvc.showBsDate; }
  protected clearFilters(): void { this.filterForm.reset({ search: '', page_size: this.pageSizeControl.value ?? 15 }, { emitEvent: false }); this.loadTokens(1); }
  protected openAddModal(): void {
    this.modalMode = 'create'; this.selectedToken = null; this.formSubmitted = false; this.submitErrorMessage = ''; this.tokenGenerated = false;
    this.tokenForm.enable({ emitEvent: false }); this.tokenForm.reset({ entity_id: null, short_code: '', token: '', valid_upto: localDateTimeValue() }); this.organisationSearchTerm = ''; this.filterEntities('');
    this.tokenForm.get('short_code')?.disable({ emitEvent: false }); this.tokenForm.get('token')?.disable({ emitEvent: false }); this.adValidUptoDate = localDateTimeValue().slice(0, 10); this.adValidUptoTime = localDateTimeValue().slice(11, 16); this.bsValidUptoTime = this.adValidUptoTime; this.modalOpen = true;
    if (this.isBsDateMode) void this.syncBsValidUptoDate();
  }
  protected openEditModal(item: ApiAccessToken): void {
    this.modalMode = 'edit'; this.selectedToken = item; this.formSubmitted = false; this.submitErrorMessage = ''; this.tokenGenerated = true;
    this.tokenForm.enable({ emitEvent: false }); this.tokenForm.reset({ entity_id: item.entity_id, short_code: item.short_code === '-' ? '' : item.short_code, token: item.token, valid_upto: toDateTimeInputValue(item.valid_upto) }); this.organisationSearchTerm = item.entity_name; this.filterEntities(this.organisationSearchTerm);
    this.tokenForm.get('short_code')?.disable({ emitEvent: false }); const validUpto = toDateTimeInputValue(item.valid_upto); this.adValidUptoDate = validUpto.slice(0, 10); this.adValidUptoTime = validUpto.slice(11, 16); this.bsValidUptoTime = this.adValidUptoTime; this.modalOpen = true;
    if (this.isBsDateMode) void this.syncBsValidUptoDate();
  }
  protected openViewModal(item: ApiAccessToken): void {
    this.openEditModal(item); this.modalMode = 'view'; this.tokenForm.disable({ emitEvent: false });
  }
  protected closeModal(): void { this.modalOpen = false; this.bsCalendarOpen = false; this.tokenForm.enable({ emitEvent: false }); this.selectedToken = null; }
  protected generateToken(): void {
    if (this.isGenerating || this.modalMode === 'view') return;
    this.isGenerating = true;
    this.httpSvc.requestCall(AuthEndPoints.GENERATE_API_ACCESS_TOKEN, ApiMethod.GET, '').pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isGenerating = false; this.cdr.markForCheck(); })).subscribe({
      next: result => { const token = result?.value?.token; if (typeof token === 'string') { this.tokenGenerated = true; this.tokenForm.get('token')?.enable({ emitEvent: false }); this.tokenForm.patchValue({ token }); } },
      error: error => this.submitErrorMessage = getApiErrorMessage(error) || 'Failed to generate API Token.',
    });
  }
  protected saveToken(): void {
    if (this.modalMode === 'view') return;
    this.formSubmitted = true;
    this.setOrganisationSelection();
    if (!this.tokenGenerated) { this.submitErrorMessage = 'Generate an API Token before saving.'; return; }
    if (this.tokenForm.invalid) { this.tokenForm.markAllAsTouched(); return; }
    const raw = this.tokenForm.getRawValue();
    const payload = this.modalMode === 'edit' && this.selectedToken ? { ...raw, id: this.selectedToken.id } : raw;
    this.httpSvc.requestCall(this.modalMode === 'edit' ? AuthEndPoints.EDIT_API_ACCESS_TOKEN : AuthEndPoints.ADD_API_ACCESS_TOKEN, this.modalMode === 'edit' ? ApiMethod.PATCH : ApiMethod.POST, payload, true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.closeModal(); this.loadTokens(this.pagination.page_no); },
      error: error => this.submitErrorMessage = getApiErrorMessage(error) || 'Failed to save API Token.',
    });
  }
  protected isActiveStatus(value: boolean | string | number | null | undefined): boolean { return typeof value === 'string' ? ['true', '1'].includes(value.trim().toLowerCase()) : value === true || value === 1; }
  protected onStatusChange(item: ApiAccessToken, active: boolean): void {
    const previous = this.isActiveStatus(item.is_active); if (previous === active) return;
    item.is_active = active;
    this.httpSvc.requestCall(AuthEndPoints.UPDATE_API_ACCESS_TOKEN_STATUS, ApiMethod.PATCH, { id: item.id, is_active: active }, true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: () => this.loadTokens(this.pagination.page_no), error: () => item.is_active = previous });
  }
  protected requiredInvalid(field: string): boolean { const control = this.tokenForm.get(field); return Boolean(control?.invalid && (control.touched || this.formSubmitted)); }
  protected onOrganisationSearch(event: ManageGeneratorAutocompleteCompleteEvent): void { this.filterEntities(event.query); this.setOrganisationSelection(event.query); }
  protected onOrganisationBlur(): void { this.setOrganisationSelection(); }
  protected openBsValidUptoPicker(): void {
    if (!this.isBsDateMode) {
      return;
    }

    this.bsCalendarOpen = true;
    void this.loadBsCalendar(this.getAdValidUptoDate(), 'AD');
  }
  protected closeBsValidUptoPicker(): void { this.bsCalendarOpen = false; }
  protected selectBsValidUptoDate(cell: BsCalendarCell): void {
    if (!cell?.ad_date || this.isBsCalendarCellDisabled(cell)) {
      return;
    }

    this.bsValidUptoDate = cell.bs_date;
    this.setAdValidUpto(cell.ad_date, this.bsValidUptoTime);
    this.closeBsValidUptoPicker();
  }
  protected shiftBsCalendarMonth(offset: number): void {
    if (!this.bsCalendarMonth || !offset) {
      return;
    }

    let month = this.bsCalendarMonth.bs_month + offset;
    let year = this.bsCalendarMonth.bs_year;
    if (month < 1) {
      month = 12;
      year -= 1;
    } else if (month > 12) {
      month = 1;
      year += 1;
    }

    void this.loadBsCalendar(`${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-01`, 'BS');
  }
  protected onBsValidUptoTimeChange(value: string): void { this.bsValidUptoTime = value; this.setAdValidUpto(this.getAdValidUptoDate(), value); }
  protected onAdValidUptoDateChange(value: string): void { this.adValidUptoDate = value; this.setAdValidUpto(value, this.adValidUptoTime); }
  protected onAdValidUptoTimeChange(value: string): void { this.adValidUptoTime = value; this.setAdValidUpto(this.adValidUptoDate, value); }
  protected isBsCalendarCellDisabled(cell: BsCalendarCell): boolean { return cell.ad_date < localDateTimeValue().slice(0, 10); }
  protected getBsTimeMin(): string { return this.getAdValidUptoDate() === localDateTimeValue().slice(0, 10) ? localDateTimeValue().slice(11, 16) : ''; }
  protected getAdTimeMin(): string { return this.adValidUptoDate === localDateTimeValue().slice(0, 10) ? localDateTimeValue().slice(11, 16) : ''; }
  protected toggleSort(field: SortField): void { this.sortOrder = this.sortBy === field && this.sortOrder === 'asc' ? 'desc' : 'asc'; this.sortBy = field; this.loadTokens(1); }
  protected goToPage(page: number): void { if (page >= 1 && page <= this.pagination.total_pages && page !== this.pagination.page_no) this.loadTokens(page); }
  protected goToPreviousPage(): void { this.goToPage(this.pagination.page_no - 1); }
  protected goToNextPage(): void { this.goToPage(this.pagination.page_no + 1); }
  protected canShowPagination(): boolean { return canShowPagination(this.pagination.total_records); }
  protected getPaginationLabel(): string { return buildPaginationLabel(this.pagination, 'No records', 'Showing', 'results'); }
  protected getPaginationItems(): number[] { return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no); }
  protected trackByTokenId(_index: number, item: ApiAccessToken): number { return item.id; }
  protected exportData(): void {
    this.isExporting = true;
    this.httpSvc.downloadfile(AuthEndPoints.EXPORT_API_ACCESS_TOKENS, ApiMethod.GET, buildQueryString({ search: this.filterForm.get('search')?.value || undefined })).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isExporting = false; this.cdr.markForCheck(); })).subscribe({ next: blob => downloadBlob(blob, buildTimestampFileName('api_access_tokens_export')), error: error => this.commonSvc.showErrorToastr(getApiErrorMessage(error) || 'Failed to export API Access Tokens.') });
  }
  @HostListener('document:click', ['$event']) protected closeMenus(event: Event): void { if (!this.host.nativeElement.contains(event.target as Node)) this.closeMobileSidebar(); }

  private loadEntities(): void { this.httpSvc.requestCall(AuthEndPoints.GET_API_ACCESS_TOKEN_ENTITIES, ApiMethod.GET, '').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: result => { this.entities = result?.value?.entities ?? []; this.filterEntities(this.organisationSearchTerm); }, error: () => { this.entities = []; this.filteredEntities = []; } }); }
  private filterEntities(value: string): void { const term = value.trim().toLowerCase(); this.filteredEntities = term ? this.entities.filter((entity) => entity.entity_name.toLowerCase().includes(term)) : [...this.entities]; }
  private setOrganisationSelection(name = this.organisationSearchTerm): void { const entity = this.entities.find((item) => item.entity_name.trim().toLowerCase() === name.trim().toLowerCase()); this.tokenForm.patchValue({ entity_id: entity?.id ?? null }, { emitEvent: true }); if (entity) this.organisationSearchTerm = entity.entity_name; }
  private updateShortCode(entityId: number | null): void { const entity = this.entities.find(item => item.id === Number(entityId)); this.tokenForm.patchValue({ short_code: entity?.short_code ?? '' }, { emitEvent: false }); }
  private listenToDatePreferenceChanges(): void {
    if (typeof window === 'undefined') {
      return;
    }

    fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.detail?.name !== 'showBsDate' || !this.modalOpen) {
          return;
        }

        this.closeBsValidUptoPicker();
        if (this.isBsDateMode) {
          void this.syncBsValidUptoDate();
        }
      });
  }
  private getAdValidUptoDate(): string { const value = this.tokenForm.get('valid_upto')?.value; return typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : localDateTimeValue().slice(0, 10); }
  private setAdValidUpto(dateValue: string, timeValue: string): void { if (dateValue && timeValue) this.tokenForm.patchValue({ valid_upto: `${dateValue}T${timeValue}` }, { emitEvent: false }); }
  private async syncBsValidUptoDate(): Promise<void> { const adDate = this.getAdValidUptoDate(); try { const response = await firstValueFrom(this.httpSvc.requestCall(AuthEndPoints.CONVERT_LOG_SHEET_DATE, ApiMethod.POST, { data_date: adDate, bs_ad_key: 'BS' }, false, { suppressToast: true })); const date = response?.value?.selected_date; this.bsValidUptoDate = typeof date === 'string' ? date : ''; } catch { this.bsValidUptoDate = ''; } finally { this.cdr.markForCheck(); } }
  private async loadBsCalendar(dateValue: string, bsAdKey: 'AD' | 'BS'): Promise<void> {
    if (!dateValue) {
      return;
    }

    this.bsCalendarLoading = true;
    try {
      const response = await firstValueFrom(this.httpSvc.requestCall(AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR, ApiMethod.POST, { data_date: dateValue, bs_ad_key: bsAdKey }, false, { suppressToast: true }));
      this.bsCalendarMonth = response?.value ?? null;
    } catch {
      this.bsCalendarMonth = null;
    } finally {
      this.bsCalendarLoading = false;
      this.cdr.markForCheck();
    }
  }
  private loadTokens(page = this.pagination.page_no): void {
    this.isLoading = true;
    const query = buildQueryString({ search: this.filterForm.get('search')?.value || undefined, page_no: page, page_size: this.pageSizeControl.value ?? 15, sort_by: this.sortBy, sort_order: this.sortOrder });
    this.httpSvc.requestCall(AuthEndPoints.GET_API_ACCESS_TOKENS, ApiMethod.GET, query).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isLoading = false; this.cdr.markForCheck(); })).subscribe({
      next: result => { const value = result?.value ?? {}; this.tokens = (value.tokens ?? []).map((item: ApiAccessToken) => ({ ...item, is_active: this.isActiveStatus(item.is_active) })); this.pagination = extractPagination<Pagination>(value) ?? { page_no: page, page_size: this.pageSizeControl.value ?? 15, total_records: 0, total_pages: 0 }; },
      error: () => { this.tokens = []; this.pagination = { page_no: page, page_size: this.pageSizeControl.value ?? 15, total_records: 0, total_pages: 0 }; },
    });
  }
}
